import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchBalances, fetchFilteredExpenses, fetchSummary } from '../services/backend-expenses';
import { getUser } from '../services/auth';
import { addDaysToYmd, periodBounds } from '../utils/istDate';
import './Dashboard.css';

const inr = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(n || 0));

const inrShort = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

function isSpend(e) {
  const t = e.transaction_type == null ? 'debit' : e.transaction_type;
  return t === 'debit';
}

function isIncome(e) {
  return e.transaction_type === 'credit';
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const Dashboard = ({ user }) => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState({});
  const [balances, setBalances] = useState([]);
  const [period, setPeriod] = useState('month');
  const [bankFilter, setBankFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('Exclude mandates');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const telegramId = getUser()?.telegram_id;

  const bounds = useMemo(() => periodBounds(period), [period]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setMessage('');
      const timestamp = Date.now();
      const { startDate, endDate } = periodBounds(period);
      const [expFiltered, summaryData, balancesRows] = await Promise.all([
        fetchFilteredExpenses(telegramId, {
          bankName: bankFilter,
          startDate,
          endDate,
          _t: timestamp
        }),
        fetchSummary(telegramId),
        fetchBalances(telegramId, { _t: timestamp })
      ]);
      setExpenses(expFiltered || []);
      setSummary(summaryData || {});
      setBalances(balancesRows || []);
    } catch (error) {
      console.error('Dashboard load error:', error);
      setMessage('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (telegramId) fetchDashboardData();
  }, [bankFilter, typeFilter, telegramId, period]);

  useEffect(() => {
    const handleFocus = () => {
      if (telegramId) fetchDashboardData();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [telegramId, bankFilter, period]);

  const visibleExpenses = useMemo(() => {
    return (expenses || []).filter((e) => {
      if (typeFilter === 'Mandates') return e.transaction_type === 'mandate';
      if (typeFilter === 'Debit') return e.transaction_type === 'debit' || !e.transaction_type;
      if (typeFilter === 'Credit') return e.transaction_type === 'credit';
      if (typeFilter === 'Exclude mandates') return e.transaction_type !== 'mandate';
      return true;
    });
  }, [expenses, typeFilter]);

  const recent = visibleExpenses.slice(0, 8);

  const totalCash = useMemo(
    () => balances.reduce((s, b) => s + Number(b.projected_balance ?? b.current_balance ?? 0), 0),
    [balances]
  );

  const periodSpend = useMemo(
    () => visibleExpenses.filter(isSpend).reduce((s, e) => s + Number(e.amount || 0), 0),
    [visibleExpenses]
  );
  const periodIncome = useMemo(
    () => visibleExpenses.filter(isIncome).reduce((s, e) => s + Number(e.amount || 0), 0),
    [visibleExpenses]
  );
  const periodSpendCount = useMemo(
    () => visibleExpenses.filter(isSpend).length,
    [visibleExpenses]
  );
  const netPeriod = periodIncome - periodSpend;
  const daySpan = useMemo(() => {
    const startMs = Date.parse(`${bounds.startDate}T00:00:00Z`);
    const endMs = Date.parse(`${bounds.endDate}T00:00:00Z`);
    return Math.max(1, Math.round((endMs - startMs) / 86400000) + 1);
  }, [bounds]);
  const pace = periodSpend / daySpan;

  const topCategories = useMemo(() => {
    const map = new Map();
    visibleExpenses.filter(isSpend).forEach((e) => {
      const name = e.category_name || 'Uncategorized';
      map.set(name, (map.get(name) || 0) + Number(e.amount || 0));
    });
    const rows = [...map.entries()].map(([name, total]) => ({ name, total })).sort((a, b) => b.total - a.total);
    const max = rows[0]?.total || 1;
    return rows.slice(0, 5).map((r) => ({ ...r, pct: Math.round((r.total / max) * 100) }));
  }, [visibleExpenses]);

  const sparkDays = useMemo(() => {
    const days = [];
    let cursor = bounds.startDate;
    while (cursor <= bounds.endDate) {
      days.push({ key: cursor, spend: 0, income: 0 });
      cursor = addDaysToYmd(cursor, 1);
    }
    // Cap spark density for long months — keep last 14 days if needed
    const sliced = days.length > 14 ? days.slice(-14) : days;
    const byKey = Object.fromEntries(sliced.map((d) => [d.key, d]));
    visibleExpenses.forEach((e) => {
      const key = e.transaction_date;
      if (!byKey[key]) return;
      const amt = Number(e.amount || 0);
      if (isSpend(e)) byKey[key].spend += amt;
      if (isIncome(e)) byKey[key].income += amt;
    });
    const max = Math.max(1, ...sliced.map((d) => Math.max(d.spend, d.income)));
    return sliced.map((d) => ({
      ...d,
      spendH: Math.max(8, Math.round((d.spend / max) * 100)),
      incomeH: Math.max(8, Math.round((d.income / max) * 100)),
      dominant: d.income > d.spend ? 'income' : 'spend'
    }));
  }, [visibleExpenses, bounds]);

  const periodNote = useMemo(() => {
    if (period === '7d') {
      return `Last 7 days · ${bounds.startDate} → ${bounds.endDate} · spend = debits only`;
    }
    const [, ms] = bounds.startDate.split('-');
    const [, , de] = bounds.endDate.split('-');
    return `Scoped to ${MONTH_SHORT[Number(ms) - 1]} 1–${Number(de)} · spend = debits only`;
  }, [bounds, period]);

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC'
    });
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-IN');

  if (loading && balances.length === 0) {
    return (
      <div className="dash-page">
        <p className="dash-eyebrow">PocketGuru · dashboard</p>
        <h1 className="dash-title">Dashboard</h1>
        <p className="dash-lead">Loading…</p>
      </div>
    );
  }

  return (
    <div className="dash-page">
      <p className="dash-eyebrow">PocketGuru · dashboard</p>
      <h1 className="dash-title">Dashboard</h1>
      <p className="dash-lead">
        Cash-first home. Settings and theme live in the nav.
      </p>

      {message && <div className="alert alert-error">{message}</div>}

      <div className="dash-hero">
        <div className="dash-panel">
          <h2>Total cash</h2>
          <p className="dash-cash">{inr(totalCash)}</p>
          <p className="dash-meta">Sum of projected balances · spend totals never include credits</p>
          {balances.length === 0 ? (
            <p className="small">No banks yet — add one in Settings.</p>
          ) : (
            <div className="dash-banks">
              {balances.map((b) => (
                <div className="dash-bank" key={b.bank_name}>
                  <div className="name">{b.bank_name}</div>
                  <div className="asof">As of {formatDateTime(b.balance_as_of)}</div>
                  <div className="bal">{inr(b.projected_balance)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="dash-panel dash-actions">
          <h2>Quick actions</h2>
          <p className="hint">Glance view only. Deep work on Analytics / Expenses.</p>
          <div className="btn-row">
            <button type="button" className="btn" onClick={() => navigate('/expenses')}>
              + Add transaction
            </button>
            <button type="button" className="btn secondary" onClick={fetchDashboardData}>
              Refresh
            </button>
            <button type="button" className="btn secondary" onClick={() => navigate('/analytics')}>
              Open Analytics
            </button>
            <button type="button" className="btn secondary" onClick={() => navigate('/expenses')}>
              Open Expenses
            </button>
          </div>
        </div>
      </div>

      <div className="dash-toolbar">
        <span className="dash-note" style={{ marginRight: 4 }}>Period</span>
        <button
          type="button"
          className={`dash-chip ${period === '7d' ? 'on' : ''}`}
          onClick={() => setPeriod('7d')}
        >
          Last 7 days
        </button>
        <button
          type="button"
          className={`dash-chip ${period === 'month' ? 'on' : ''}`}
          onClick={() => setPeriod('month')}
        >
          This month
        </button>
        <span className="dash-divider" aria-hidden="true" />
        <button
          type="button"
          className={`dash-chip ${bankFilter === 'All' ? 'on' : ''}`}
          onClick={() => setBankFilter('All')}
        >
          All banks
        </button>
        {balances.map((b) => (
          <button
            key={b.bank_name}
            type="button"
            className={`dash-chip ${bankFilter === b.bank_name ? 'on' : ''}`}
            onClick={() => setBankFilter(b.bank_name)}
          >
            {b.bank_name}
          </button>
        ))}
        <select
          className="dash-field"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option>Exclude mandates</option>
          <option>Debit</option>
          <option>Credit</option>
          <option>All</option>
          <option>Mandates</option>
        </select>
        <span className="dash-spacer" />
        <span className="dash-note">{periodNote}</span>
      </div>

      <div className="dash-kpis">
        <div className="dash-kpi">
          <div className="label">Today’s spend</div>
          <div className="value debit">{inr(summary.todayTotal || 0)}</div>
          <div className="delta">
            {summary.todaySpendCount != null ? summary.todaySpendCount : '—'} debits · credits excluded
          </div>
        </div>
        <div className="dash-kpi">
          <div className="label">Period spend</div>
          <div className="value debit">{inr(periodSpend)}</div>
          <div className="delta">
            {periodSpendCount} debit transactions · {period === '7d' ? 'last 7 days' : 'this month'}
          </div>
        </div>
        <div className="dash-kpi">
          <div className="label">Period income</div>
          <div className="value credit">{inr(periodIncome)}</div>
          <div className="delta">Credits in selected period</div>
        </div>
        <div className="dash-kpi">
          <div className="label">Net</div>
          <div className={`value ${netPeriod >= 0 ? 'credit' : 'debit'}`}>
            {netPeriod >= 0 ? '+' : ''}
            {inr(netPeriod)}
          </div>
          <div className="delta">income − spend</div>
        </div>
        <div className="dash-kpi">
          <div className="label">Pace</div>
          <div className="value">
            {inrShort(pace)}
            <span style={{ fontSize: 13, color: 'var(--pg-muted)' }}>/day</span>
          </div>
          <div className="delta">avg daily in period</div>
        </div>
      </div>

      <div className="dash-mid">
        <div className="dash-panel">
          <p className="title">
            {period === '7d' ? 'Last 7 days · cash movement' : 'This month · cash movement'}
          </p>
          <p className="sub">Bars follow the period toggle · full history on Analytics</p>
          <div className="dash-spark" aria-hidden="true">
            {sparkDays.map((d) => (
              <span
                key={d.key}
                className={d.dominant === 'income' ? 'income' : ''}
                style={{ height: `${d.dominant === 'income' ? d.incomeH : d.spendH}%` }}
                title={`${d.key}: spend ${d.spend} / income ${d.income}`}
              />
            ))}
          </div>
          <div className="dash-spark-legend">
            <span>
              <i style={{ background: 'var(--pg-accent)' }} />
              Spend
            </span>
            <span>
              <i style={{ background: 'var(--pg-good)' }} />
              Income
            </span>
          </div>
        </div>
        <div className="dash-panel">
          <p className="title">
            Top categories · {period === '7d' ? 'last 7 days' : 'this month'}
          </p>
          <p className="sub">Debits only · top 5 · same period as KPIs</p>
          {topCategories.length === 0 ? (
            <p className="small">No spend in this period yet.</p>
          ) : (
            topCategories.map((c) => (
              <div className="dash-cat-row" key={c.name}>
                <span>{c.name}</span>
                <div className="bar">
                  <i style={{ width: `${c.pct}%` }} />
                </div>
                <span className="amt">{inrShort(c.total)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="dash-panel">
        <div className="dash-tx-head">
          <div>
            <p className="title" style={{ margin: 0 }}>
              Recent activity
            </p>
            <p className="sub" style={{ margin: '4px 0 0' }}>
              Last {recent.length} of {visibleExpenses.length} in period
            </p>
          </div>
          <button type="button" className="dash-link" onClick={() => navigate('/expenses')}>
            View all on Expenses →
          </button>
        </div>
        {recent.length === 0 ? (
          <p className="small" style={{ padding: '12px 0' }}>
            No transactions in this period.
          </p>
        ) : (
          <ul className="dash-tx-list">
            {recent.map((expense) => {
              const type = expense.transaction_type || 'debit';
              const credit = type === 'credit';
              const debit = type === 'debit' || !expense.transaction_type;
              return (
                <li key={expense.id}>
                  <div>
                    <div className="merchant">
                      {expense.merchant}{' '}
                      <span className={`type-pill ${credit ? 'credit' : debit ? 'debit' : ''}`}>{type}</span>
                    </div>
                    <div className="meta">
                      {expense.category_name || 'Uncategorized'} · {formatDate(expense.transaction_date)}
                    </div>
                  </div>
                  <div>
                    <div className={`amt ${credit ? 'credit' : debit ? 'debit' : ''}`}>
                      {credit ? '+' : debit ? '−' : ''}
                      {inr(expense.amount)}
                    </div>
                    <div className="bank-tag">{expense.bank}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
